import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "react-toastify";
import { describeApiError } from "../../../api/client";
import "../OpsShared.css";
import "../../Team/people.css";
import "./sops.css";
import "../../../theme/print-sop.css";
import {
  listSops, getSop, createSop, updateSop, setPublished, listSopVersions,
  sendSop, getSopAcknowledgments, acknowledgeSop, downloadSopPdf, getSopPdfLink, reviseSop, getSopReview, approveSopReview, draftSop,
} from "../../../api/sopsApi";
import { getUserRole } from "../../../helpers/tenantHelpers";
import SopAskPanel from "./SopAskPanel";
import QuestionQueue from "../Documents/QuestionQueue";

/* People & Operations → SOPs: a document viewer, not a textarea.
   - Markdown rendered (same markdown the editor saves). The inferred drafts'
     provenance blockquote, evidence footer and "Open questions for the manager"
     block are recognised and styled: questions become a checklist a manager
     works through (ticks are local until the SOP is edited into a new version).
   - DRAFT vs PUBLISHED is loud: published is what the assistant cites.
   - Send (email / push) "read and acknowledge" — published only; who has read
     it is D's hr_app.SOPAcknowledgment on the HR anchor (version-pinned).
   - Version history with a line diff; print-clean; PDF for the binder. */

const AUTHOR_ROLES = new Set(["owner", "admin", "manager"]);
const CATEGORIES = [
  ["service", "Service / Field"], ["safety", "Safety / Compliance"], ["scheduling", "Scheduling / Dispatch"],
  ["sales", "Sales / Quoting"], ["hr", "HR / People"], ["admin", "Office / Admin"], ["general", "General"],
];
const catLabel = (k) => (CATEGORIES.find(([c]) => c === k) || [k, String(k || "general").replace(/_/g, " ")])[1];
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

/* Split a body into the parts the viewer treats specially. Everything the
   splitter doesn't recognise stays in `main` and renders as markdown. */
function splitBody(body) {
  const text = String(body || "").replace(/\r\n/g, "\n");
  let main = text, evidence = [], questions = [];
  const qIdx = text.search(/\*\*Open questions for the manager\*\*[^\n]*\n/i);
  if (qIdx >= 0) {
    const after = text.slice(qIdx);
    const nl = after.indexOf("\n");
    const block = after.slice(nl + 1);
    questions = block.split("\n").map((l) => l.trim()).filter((l) => /^([-*+]|\d+[.)])\s+/.test(l))
      .map((l) => l.replace(/^([-*+]|\d+[.)])\s+/, "").replace(/^\[( |x|X)\]\s*/, "").trim()).filter(Boolean);
    main = text.slice(0, qIdx);
  }
  const lines = main.split("\n");
  const keep = [];
  for (const l of lines) {
    if (/^_Evidence sample[^_]*_\s*$/i.test(l.trim()) || /^_?Evidence[^_\n]*\._?\s*$/i.test(l.trim())) evidence.push(l.trim().replace(/^_|_$/g, ""));
    else keep.push(l);
  }
  main = keep.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  return { main, evidence, questions };
}

/* Line diff (LCS) between two bodies — small documents, so O(n·m) is fine. */
function lineDiff(a, b) {
  const A = String(a || "").split("\n"), B = String(b || "").split("\n");
  const n = A.length, m = B.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = []; let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ t: "eq", s: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: "del", s: A[i] }); i++; }
    else { out.push({ t: "add", s: B[j] }); j++; }
  }
  while (i < n) out.push({ t: "del", s: A[i++] });
  while (j < m) out.push({ t: "add", s: B[j++] });
  return out;
}

const qKey = (slug, version) => `sop.oq.${slug}.v${version}`;
/* unified diff (B's review turns) → line rows for the diff block */
const parseUnified = (text) => String(text || "").split("\n").filter((l) => l && !/^(---|\+\+\+|@@)/.test(l))
  .map((l) => (l.startsWith("+") ? { t: "add", s: l.slice(1) } : l.startsWith("-") ? { t: "del", s: l.slice(1) } : null)).filter(Boolean);
const readTicks = (slug, version) => { try { return JSON.parse(localStorage.getItem(qKey(slug, version)) || "[]"); } catch { return []; } };

export default function SopsPage() {
  const role = String(getUserRole("Member") || "").toLowerCase();
  const canAuthor = AUTHOR_ROLES.has(role);
  const location = useLocation();
  const navigate = useNavigate();

  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [slug, setSlug] = useState(() => new URLSearchParams(location.search).get("open") || null);
  const [doc, setDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [panel, setPanel] = useState(null); // 'history' | 'acks' | 'send' | null
  const [editing, setEditing] = useState(null); // {mode:'new'|'edit', title, category, body, is_published}
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState(null);
  const [diffPick, setDiffPick] = useState(null); // version number to diff against its predecessor
  const [acks, setAcks] = useState(null);
  const [ticks, setTicks] = useState([]);
  const [sendForm, setSendForm] = useState({ group: "all", user_ids: [], channels: ["email", "push"], note: "" });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [maker, setMaker] = useState(null);   // {title, category, notes, busy} — the AI SOP maker
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [acking, setAcking] = useState(false);
  // Review thread (docs/SOP_REVIEW_THREAD_CONTRACT_2026-09-08.md): open
  // questions become assistant messages; the manager answers or asks for a
  // change; B's redraft appends an unpublished version; Approve publishes.
  const [review, setReview] = useState(null);        // GET review/ payload (server-side thread)
  const [composer, setComposer] = useState("");
  const [revising, setRevising] = useState(false);
  const [reviewNote, setReviewNote] = useState(null);  // transient system line (502 / pending approval)
  const [openDiffSeq, setOpenDiffSeq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listSops(); setSops(r.data?.sops || (Array.isArray(r.data) ? r.data : [])); setError(null); }
    catch (e) { setError(e?.status === 403 ? "You don’t have access to SOPs." : "Couldn’t load SOPs."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDoc = useCallback(async (s) => {
    if (!s) { setDoc(null); return; }
    setDocLoading(true); setPanel(null); setVersions(null); setAcks(null); setSendResult(null); setDiffPick(null);
    try {
      let r;
      try { r = await getSop(s); }
      catch (first) {
        if (first?.status && first.status !== 401 && first.status !== 429) throw first;
        await new Promise((ok) => setTimeout(ok, 800)); // one retry: a token refresh or a transient miss right after load
        r = await getSop(s);
      }
      const d = r.data?.sop || r.data;
      setDoc(d); setTicks(readTicks(d.slug, d.version));
      navigate({ search: `?open=${encodeURIComponent(s)}` }, { replace: true });
    } catch { toast.error("Couldn’t open that SOP."); setDoc(null); }
    finally { setDocLoading(false); }
  }, [navigate]);
  useEffect(() => { if (slug) openDoc(slug); }, [slug, openDoc]);

  const parts = useMemo(() => (doc ? splitBody(doc.body) : null), [doc]);
  const grouped = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = sops.filter((x) => !s || [x.title, x.category, ...(x.tags || [])].some((v) => String(v || "").toLowerCase().includes(s)));
    const by = {};
    list.forEach((x) => { (by[x.category || "general"] ||= []).push(x); });
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [sops, q]);

  /* ── actions ── */
  const togglePublish = async () => {
    if (!doc) return;
    const next = !doc.is_published;
    if (next && !window.confirm(`Publish "${doc.title}" v${doc.version}? Published SOPs are cited by the assistant to every employee.`)) return;
    try { const r = await setPublished(doc.slug, next); const d = r.data?.sop || r.data; setDoc(d); await load(); toast.success(next ? "Published — the assistant can cite it now" : "Unpublished — the assistant no longer cites it"); }
    catch (e) { toast.error(e?.status === 403 ? "Only an admin or manager can publish." : "Couldn’t change publish state."); }
  };
  /* The AI SOP maker: type what the procedure is, get a real first draft from
     the tenant's own records, their other SOPs, the industry pack and (marked)
     standard practice — then the same review conversation. */
  const runMaker = async (e) => {
    e.preventDefault();
    const title = (maker?.title || "").trim();
    if (!title) { toast.error("Type what the procedure is — e.g. “Handling a callback within 24 hours”."); return; }
    setMaker((m) => ({ ...m, busy: true }));
    try {
      const r = await draftSop({ title, category: maker.category, notes: maker.notes });
      const d = r.data;
      toast.success(`Drafted “${d.title}” — ${d.grounded_steps} of ${d.total_steps} steps from your own records`);
      setMaker(null);
      await load();
      setSlug(d.slug);
      await openDoc(d.slug);
      setPanel("review");
    } catch (ex) {
      toast.error(describeApiError(ex, { forbidden: "Only an admin or manager can draft SOPs.",
        fallback: "The drafter didn’t answer — nothing was written. Try again in a moment." }));
      setMaker((m) => (m ? { ...m, busy: false } : m));
    }
  };

  const startEdit = (mode) => {
    if (mode === "new") setEditing({ mode, title: "", category: "general", body: "## When this applies\n\n## Steps\n1. …\n", is_published: false });
    else setEditing({ mode: "edit", title: doc.title, category: doc.category, body: doc.body, is_published: !!doc.is_published });
    setPanel(null);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing.title.trim() || !editing.body.trim()) { toast.error("Give it a title and a body."); return; }
    setSaving(true);
    try {
      const body = { title: editing.title.trim(), category: editing.category, body: editing.body, is_published: editing.is_published };
      let d;
      if (editing.mode === "new") { const r = await createSop(body); d = r.data?.sop || r.data; }
      else { const r = await updateSop(doc.slug, body); d = r.data?.sop || r.data; }
      toast.success(editing.mode === "new" ? "SOP created" : `Saved as v${d.version}`);
      setEditing(null); await load(); setSlug(d.slug); setDoc(d); setTicks(readTicks(d.slug, d.version));
    } catch (ex) { toast.error(ex?.status === 403 ? "Only an admin or manager can author SOPs." : ex?.payload?.detail || ex?.payload?.error || "Couldn’t save."); }
    finally { setSaving(false); }
  };
  const openHistory = async () => {
    if (panel === "history") { setPanel(null); return; }
    setPanel("history");
    if (!versions) { try { const r = await listSopVersions(doc.slug); setVersions(r.data?.versions || []); } catch { toast.error("Couldn’t load history."); } }
  };
  const openAcks = async () => {
    if (panel === "acks") { setPanel(null); return; }
    setPanel("acks"); await refreshAcks();
  };
  const refreshAcks = async () => {
    try { const r = await getSopAcknowledgments(doc.slug); setAcks(r.data); } catch { toast.error("Couldn’t load acknowledgments."); }
  };
  const openSend = async () => {
    if (panel === "send") { setPanel(null); return; }
    setPanel("send"); setSendResult(null);
    setTimeout(() => document.querySelector(".sop-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    if (!acks) await refreshAcks();
  };
  const doSend = async (e) => {
    e.preventDefault();
    if (!sendForm.channels.length) { toast.error("Pick email, push, or SMS."); return; }
    if (sendForm.group === "pick" && !sendForm.user_ids.length) { toast.error("Pick at least one person."); return; }
    setSending(true);
    try {
      const body = { channels: sendForm.channels, note: sendForm.note || "" };
      if (sendForm.group === "pick") body.user_ids = sendForm.user_ids; else body.group = sendForm.group;
      const r = await sendSop(doc.slug, body); setSendResult(r.data);
      if (r.data?.share) setDoc((d) => (d ? { ...d, share: r.data.share } : d));
      toast.success(r.data?.summary || `Sent to ${r.data?.delivered_to ?? 0} of ${r.data?.total ?? 0}`); await refreshAcks();
    } catch (ex) { toast.error(describeApiError(ex, { conflict: "Only a published version can be sent — publish it first.", forbidden: "Only an admin or manager can send SOPs.", fallback: "The send didn’t go — nothing was recorded." })); }
    finally { setSending(false); }
  };
  const doAcknowledge = async () => {
    setAcking(true);
    try { await acknowledgeSop(doc.slug); toast.success(`Acknowledged v${doc.version}`); await refreshAcks(); }
    catch (ex) { toast.error(ex?.payload?.detail || (ex?.status === 404 ? "Only a published SOP can be acknowledged." : "Couldn’t record your acknowledgment.")); }
    finally { setAcking(false); }
  };
  const doPrint = () => {
    document.body.classList.add("sop-print-mode");
    const off = () => { document.body.classList.remove("sop-print-mode"); window.removeEventListener("afterprint", off); };
    window.addEventListener("afterprint", off);
    window.print();
    setTimeout(off, 2000);
  };
  const doPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await getSopPdfLink(doc.slug);
      const url = r.data?.url;
      if (url) {
        const w = window.open(url, "_blank", "noopener");
        if (!w) await downloadSopPdf(doc.slug, r.data?.filename);
        toast.success(`PDF ready — ${r.data?.filename}${r.data?.branding?.logo === false ? " (no logo on file: add one under Settings → Branding)" : ""}`);
      } else throw new Error("no url");
    } catch (ex) {
      if (ex?.status === 409) {
        try { await downloadSopPdf(doc.slug, `${doc.slug}-v${doc.version}-draft.pdf`); toast.success("Draft PDF downloaded."); }
        catch (e2) { toast.error(describeApiError(e2, { fallback: "Couldn’t build the PDF." })); }
      } else toast.error(describeApiError(ex, { fallback: "Couldn’t build the PDF." }));
    } finally { setPdfBusy(false); }
  };
  const toggleTick = (i) => {
    const next = ticks.includes(i) ? ticks.filter((x) => x !== i) : [...ticks, i];
    setTicks(next); try { localStorage.setItem(qKey(doc.slug, doc.version), JSON.stringify(next)); } catch {}
  };

  /* ── review conversation (B's loop; server-side thread) ── */
  const loadReview = async (slugArg) => {
    const target = slugArg || doc.slug;
    try {
      let r;
      try { r = await getSopReview(target); }
      catch (first) {
        if (first?.status === 404 || first?.status === 403) throw first;
        await new Promise((ok) => setTimeout(ok, 800)); // first request after a load can miss; one retry
        r = await getSopReview(target);
      }
      setReview(r.data); setReviewNote(null);
    }
    catch (ex) { setReview(null); setReviewNote(ex?.status === 404 ? "The review loop isn’t deployed for this SOP yet — a manager can still edit it by hand." : "Couldn’t load the review thread."); }
  };
  useEffect(() => { setReview(null); setReviewNote(null); setOpenDiffSeq(null); }, [doc?.slug]); // eslint-disable-line react-hooks/exhaustive-deps
  const openReview = async () => {
    if (panel === "review") { setPanel(null); return; }
    setPanel("review");
    if (doc) await loadReview(doc.slug);
  };
  const sendReview = async (e) => {
    e?.preventDefault?.();
    const message = composer.trim();
    if (!message) { toast.error("Answer the question or type a change."); return; }
    setComposer(""); setRevising(true); setReviewNote(null);
    // optimistic manager turn so the thread reads as a conversation while the redraft runs
    setReview((r) => (r ? { ...r, turns: [...(r.turns || []), { seq: `tmp-${Date.now()}`, role: "manager", text: message, pending: true }] } : r));
    try {
      const r = await reviseSop(doc.slug, { message });
      const out = r.data || {};
      const fresh = await getSop(doc.slug); const d = fresh.data?.sop || fresh.data;
      setDoc(d); await load(); await loadReview(d.slug);
      if (out.turn?.seq) setOpenDiffSeq(out.turn.seq);
      if (out.was_published && !out.is_published) setReviewNote(`Revision pending your approval — v${out.version} is not used by the assistant until you approve it.`);
      toast.success(`Redrafted as v${out.version}`);
    } catch (ex) {
      setReview((r) => (r ? { ...r, turns: (r.turns || []).filter((t) => !t.pending) } : r));
      setComposer(message);
      if (ex?.status === 502) setReviewNote("The redraft model didn’t answer — nothing was written. Try again in a moment.");
      else if (ex?.status === 404) setReviewNote("The review loop isn’t deployed for this SOP yet.");
      else toast.error(ex?.payload?.detail || ex?.payload?.error || (ex?.status === 403 ? "Only an admin or manager can review SOPs." : "Couldn’t redraft."));
    } finally { setRevising(false); }
  };
  const approve = async () => {
    const remaining = review?.remaining ?? parts?.questions?.length ?? 0;
    const msg = remaining
      ? `Publish v${doc.version} with ${remaining} open question${remaining === 1 ? "" : "s"} still in the document? The assistant will cite it to every employee.`
      : `Publish v${doc.version}? The assistant cites published SOPs to every employee.`;
    if (!window.confirm(msg)) return;
    try {
      await approveSopReview(doc.slug);
      const fresh = await getSop(doc.slug); const d = fresh.data?.sop || fresh.data;
      setDoc(d); await load(); await loadReview(d.slug);
      toast.success(`Published v${d.version} — the assistant cites it now`);
    } catch (e) { toast.error(e?.status === 403 ? "Only an admin or manager can publish." : "Couldn’t publish."); }
  };

  const me = acks?.me || null;
  const iAcknowledged = !!(me && me.current);

  return (
    <div className="ops-page">
      <header className="ops-head">
        <div>
          <h1>SOPs</h1>
          <p className="ops-muted">Documented procedures. <b>Published</b> SOPs are what the assistant cites to every employee; a <b>draft</b> is invisible to it until a manager publishes.</p>
        </div>
        <div className="ops-actions">
          {canAuthor && <button className="ops-btn" onClick={() => setMaker({ title: "", category: "general", notes: "", busy: false })}>New SOP</button>}
        </div>
      </header>

      {error ? <div className="ppl-note danger">{error}</div> : null}
      <QuestionQueue compact openOnly />

      <div className={`sop-layout ${panel === "review" && doc && !editing ? "has-review" : ""}`}>
        {/* ── library ── */}
        <aside className="ppl-card sop-library">
          <h3>Library</h3>
          <input className="sop-search" placeholder="Search title, category, tag" value={q} onChange={(e) => setQ(e.target.value)} />
          {loading ? <div className="ppl-muted">Loading…</div>
            : sops.length === 0 ? (
              <div className="ppl-empty">
                <strong>No SOPs yet.</strong>
                <span>{canAuthor ? "Write the first one — a callback, a no-show, a chemical spill. The assistant cites it the moment it's published." : "Nothing documented yet. Ask a manager to add your procedures."}</span>
              </div>
            ) : grouped.map(([cat, items]) => (
              <div className="sop-group" key={cat}>
                <div className="sop-group-title">{catLabel(cat)}</div>
                {items.map((s) => (
                  <button key={s.slug} className={`sop-row ${doc?.slug === s.slug ? "is-open" : ""}`} onClick={() => setSlug(s.slug)}>
                    <div className="sop-row-main">
                      <span className="ppl-name">{s.title}</span>
                      <span className="ppl-sub">v{s.version} · {fmtDate(s.updated_at)}</span>
                    </div>
                    <span className={`ppl-pill ${s.is_published ? "approved" : "pending"}`}>{s.is_published ? "published" : "draft"}</span>
                  </button>
                ))}
              </div>
            ))}
        </aside>

        {/* ── document / editor ── */}
        <main>
          {editing ? (
            <section className="ppl-card">
              <h3>{editing.mode === "new" ? "New SOP" : `Edit “${doc?.title}” — saves as v${(doc?.version || 0) + 1}`}</h3>
              <form className="ppl-form" onSubmit={saveEdit}>
                <label className="ppl-form--wide"><span>Title</span><input type="text" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. Handling a callback within 24 hours" /></label>
                <label><span>Category</span>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                    {CATEGORIES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                  </select>
                </label>
                <label className="ppl-check"><input type="checkbox" checked={editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} /><span>Published — the assistant may cite this version</span></label>
                <div className="ppl-form--wide sop-editor-grid">
                  <label><span>Body <em>markdown</em></span><textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} /></label>
                  <div><span className="ppl-sub">Preview</span><div className="sop-preview sop-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{editing.body}</ReactMarkdown></div></div>
                </div>
                <div className="ppl-form--wide ppl-actions">
                  <button type="submit" className="ops-btn" disabled={saving}>{saving ? "Saving…" : editing.mode === "new" ? "Create" : "Save new version"}</button>
                  <button type="button" className="ops-btn secondary" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </form>
            </section>
          ) : maker ? (
            <section className="ppl-card sop-maker">
              <h3>New SOP</h3>
              <p className="ppl-sub">Type what the procedure is. Abon writes a first draft from your own records, your other SOPs and what your trade requires — then asks you the questions it couldn’t answer. Nothing is published until you approve it.</p>
              <form onSubmit={runMaker}>
                <input autoFocus value={maker.title} disabled={maker.busy} placeholder="e.g. Handling a callback within 24 hours"
                  onChange={(e) => setMaker({ ...maker, title: e.target.value })} />
                <div className="row">
                  <select value={maker.category} disabled={maker.busy} onChange={(e) => setMaker({ ...maker, category: e.target.value })}>
                    {CATEGORIES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                  </select>
                  <input value={maker.notes} disabled={maker.busy} placeholder="Anything it should know (optional)"
                    onChange={(e) => setMaker({ ...maker, notes: e.target.value })} />
                </div>
                <div className="ppl-actions">
                  <button type="submit" className="ops-btn" disabled={maker.busy}>{maker.busy ? "Drafting…" : "Draft it"}</button>
                  <button type="button" className="ops-btn secondary" disabled={maker.busy} onClick={() => { setMaker(null); startEdit("new"); }}>Start blank instead</button>
                  <button type="button" className="ops-btn secondary" disabled={maker.busy} onClick={() => setMaker(null)}>Cancel</button>
                </div>
              </form>
            </section>
          ) : !doc ? (
            <section className="ppl-card">
              <div className="ppl-empty"><strong>{docLoading ? "Opening…" : "Pick an SOP"}</strong><span>Read it, print it, send it for acknowledgment, or see what changed between versions.</span></div>
            </section>
          ) : (
            <>
              <div className={`sop-banner ${doc.is_published ? "is-published" : "is-draft"}`}>
                <strong>{doc.is_published ? "Published" : "Draft"}</strong>
                <div className="sop-banner-text">
                  {doc.is_published
                    ? <>Version {doc.version} is live. The assistant cites this document when staff ask about it, and it can be sent for acknowledgment.</>
                    : <>Not visible to the assistant and can’t be sent or acknowledged until a manager publishes it. {canAuthor ? "Review it, resolve the open questions, then publish." : "A manager has to review and publish it."}</>}
                </div>
                {canAuthor && <button className="ops-btn" onClick={togglePublish}>{doc.is_published ? "Unpublish" : "Publish v" + doc.version}</button>}
              </div>

              <article className="sop-doc">
                <div className="sop-doc-head">
                  <div>
                    <h2>{doc.title}</h2>
                    <div className="sop-doc-meta">{catLabel(doc.category)} · v{doc.version} · updated {fmtDate(doc.updated_at)}{doc.updated_by ? ` by ${doc.updated_by}` : ""}{doc.tags?.length ? ` · ${doc.tags.join(", ")}` : ""}</div>
                    {doc.share && (
                      <div className="sop-doc-meta sop-share-line" title="Sent / acknowledged / signed for this version">
                        <strong>{doc.share.line}</strong>
                        {doc.share.last_send ? ` — ${doc.share.last_send.summary}${doc.share.last_send.sent_by ? ` by ${doc.share.last_send.sent_by}` : ""}` : doc.is_published ? " — not sent yet" : ""}
                      </div>
                    )}
                  </div>
                </div>
                <div className="sop-toolbar">
                  {canAuthor && <button className="ops-btn secondary" onClick={() => startEdit("edit")}>Edit</button>}
                  {canAuthor && <button className={`ops-btn secondary ${panel === "send" ? "is-active" : ""}`} onClick={openSend} title={doc.is_published ? "" : "Publish first"}>Send</button>}
                  <button className="ops-btn secondary" onClick={doPrint}>Print</button>
                  <button className="ops-btn secondary" onClick={doPdf} disabled={pdfBusy}>{pdfBusy ? "PDF…" : "PDF"}</button>
                  {canAuthor && <button className={`ops-btn ${panel === "review" ? "" : "secondary"}`} onClick={openReview}>{doc.is_published ? "Review" : `Review${(review?.remaining ?? parts.questions.length) ? ` (${review?.remaining ?? parts.questions.length} open)` : ""}`}</button>}
                  <button className={`ops-btn secondary ${panel === "history" ? "is-active" : ""}`} onClick={openHistory}>History{doc.version > 1 ? ` (${doc.version})` : ""}</button>
                  <button className={`ops-btn secondary ${panel === "acks" ? "is-active" : ""}`} onClick={openAcks}>Acknowledgments{acks ? ` ${acks.acknowledged_current}/${acks.total}` : ""}</button>
                </div>

                <div className="sop-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{parts.main}</ReactMarkdown>
                </div>

                {parts.questions.length > 0 && (
                  <div className="sop-questions">
                    <h4>Open questions for the manager — {ticks.length}/{parts.questions.length} settled</h4>
                    <div className="ppl-muted">The records didn’t settle these. Tick each one as you decide it, then edit the SOP so the answer is in the procedure.</div>
                    {parts.questions.map((question, i) => (
                      <label key={i} className={`sop-q ${ticks.includes(i) ? "is-done" : ""}`}>
                        <input type="checkbox" checked={ticks.includes(i)} onChange={() => toggleTick(i)} />
                        <span>{question}</span>
                      </label>
                    ))}
                    {canAuthor && <div className="sop-q-actions"><button className="ops-btn" onClick={() => startEdit("edit")}>Resolve in a new version</button></div>}
                  </div>
                )}

                {parts.evidence.length > 0 && (
                  <div className="sop-evidence">
                    <div className="sop-evidence-title">Evidence</div>
                    {parts.evidence.map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}

                {doc.is_published && (
                  <div className={`sop-ack-self ${iAcknowledged ? "is-done" : ""}`}>
                    <span>{iAcknowledged ? `You acknowledged v${me.acknowledged_version} on ${fmtDate(me.acknowledged_at)}.` : me === null && acks ? "Your account isn’t on the employee roster, so an acknowledgment has nowhere to land." : me?.needs_reack ? `This SOP changed since you acknowledged v${me.acknowledged_version} — read and re-acknowledge v${doc.version}.` : "Read it? Acknowledge this version so your manager can see it."}</span>
                    {!iAcknowledged && (me || !acks) && <button className="ops-btn" onClick={doAcknowledge} disabled={acking}>{acking ? "Saving…" : me?.needs_reack ? `Re-acknowledge v${doc.version}` : "I’ve read this"}</button>}
                  </div>
                )}
              </article>

              {/* Ask about THIS document. Directly under the text, so the section
                  a citation names is a scroll away and can be checked. */}
              <SopAskPanel slug={doc.slug} title={doc.title} />

              {panel === "history" && (
                <section className="sop-panel">
                  <h3>Version history</h3>
                  {!versions ? <div className="ppl-muted">Loading…</div> : (
                    <>
                      {versions.map((v) => (
                        <div key={v.version} className="sop-version">
                          <span className="v">v{v.version}</span>
                          <span className={`ppl-pill ${v.is_published ? "approved" : "pending"}`}>{v.is_published ? "published" : "draft"}</span>
                          <span className="who">{fmtDateTime(v.updated_at)}{v.updated_by ? ` · ${v.updated_by}` : ""}{v.is_current ? " · current" : ""}</span>
                          {v.version > 1 && <button className="ops-btn secondary" onClick={() => setDiffPick(diffPick === v.version ? null : v.version)}>{diffPick === v.version ? "Hide changes" : `What changed in v${v.version}`}</button>}
                        </div>
                      ))}
                      {diffPick && (() => {
                        const cur = versions.find((v) => v.version === diffPick);
                        const prev = versions.find((v) => v.version === diffPick - 1);
                        if (!cur || !prev) return null;
                        const d = lineDiff(prev.body, cur.body).filter((l) => l.t !== "eq" || true);
                        const changed = d.filter((l) => l.t !== "eq").length;
                        return (
                          <div className="sop-diff">
                            {changed === 0 ? <div className="sop-diff-empty">Body unchanged (title, category, tags or publish state changed).</div>
                              : d.map((l, i) => (l.t === "eq" ? null : <div key={i} className={`sop-diff-line ${l.t}`}>{(l.t === "add" ? "+ " : "− ") + l.s}</div>))}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </section>
              )}

              {panel === "acks" && (
                <section className="sop-panel">
                  <h3>Who has read v{doc.version}</h3>
                  {!acks ? <div className="ppl-muted">Loading…</div> : (
                    <>
                      <div className="sop-ack-summary"><span><b>{acks.acknowledged_current}</b> of <b>{acks.total}</b> acknowledged this version</span><span><b>{acks.sent}</b> sent</span></div>
                      {acks.total === 0 ? <div className="ppl-muted">No employees on the roster yet (People & Operations → Employees).</div> : (
                        <>
                          <div className="sop-ack-row head"><span>Employee</span><span>Sent</span><span>Acknowledged</span></div>
                          {acks.employees.map((e) => (
                            <div key={e.employee_id} className="sop-ack-row">
                              <span><span className="ppl-name">{e.name}</span><span className="ppl-sub">{e.role || e.email}</span></span>
                              <span className="ppl-sub">{e.sent_at ? fmtDateTime(e.sent_at) : "—"}</span>
                              <span>{e.current ? <span className="ppl-pill approved">v{e.acknowledged_version} · {fmtDate(e.acknowledged_at)}</span> : e.acknowledged_version ? <span className="ppl-pill pending">v{e.acknowledged_version} (old)</span> : <span className="ppl-pill">not yet</span>}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </section>
              )}

              {panel === "send" && (
                <section className="sop-panel">
                  <h3>Send for acknowledgment</h3>
                  {!doc.is_published ? <div className="ppl-note danger">This is a draft. Publish it first — staff are only ever asked to acknowledge the version the assistant cites.</div> : (
                    <form className="sop-send" onSubmit={doSend}>
                      <div className="row">
                        <span>To:</span>
                        {[["all", "Everyone"], ["field", "Field techs"], ["office", "Office"], ["pick", "Pick people"], ["me", "Just me (test)"]].map(([g, l]) => (
                          <label key={g} className="chip"><input type="radio" name="grp" checked={sendForm.group === g} onChange={() => setSendForm({ ...sendForm, group: g })} />{l}</label>
                        ))}
                      </div>
                      {sendForm.group === "pick" && (
                        <div className="people">
                          {(acks?.employees || []).map((e) => (
                            <label key={e.user_id}><input type="checkbox" checked={sendForm.user_ids.includes(e.user_id)} onChange={(ev) => setSendForm({ ...sendForm, user_ids: ev.target.checked ? [...sendForm.user_ids, e.user_id] : sendForm.user_ids.filter((x) => x !== e.user_id) })} />{e.name} <span className="ppl-sub">{e.email}</span></label>
                          ))}
                          {!acks?.employees?.length && <span className="ppl-muted">No employees on the roster.</span>}
                        </div>
                      )}
                      <div className="row">
                        <span>Via:</span>
                        {[["email", "Email + PDF"], ["push", "Mobile push"], ["sms", "SMS link"]].map(([c, l]) => (
                          <label key={c} className="chip"><input type="checkbox" checked={sendForm.channels.includes(c)} onChange={(ev) => setSendForm({ ...sendForm, channels: ev.target.checked ? [...sendForm.channels, c] : sendForm.channels.filter((x) => x !== c) })} />{l}</label>
                        ))}
                      </div>
                      <textarea rows={2} placeholder="Optional note — e.g. “Effective Monday. Read before your first stop.”" value={sendForm.note} onChange={(e) => setSendForm({ ...sendForm, note: e.target.value })} />
                      <div className="ppl-actions">
                        <button type="submit" className="ops-btn" disabled={sending}>{sending ? "Sending…" : "Send “read and acknowledge”"}</button>
                        <span className="ppl-muted">Email goes from your company’s sending address with the branded PDF attached. Push reaches devices signed in to the Abon app. SMS sends a link to the staff phone on file.</span>
                      </div>
                      {sendResult && (
                        <div className="sop-send-result">
                          <b>{sendResult.summary || `Delivered to ${sendResult.delivered_to} of ${sendResult.total}.`}</b>
                          {sendResult.branding?.note && <span className="ppl-sub">{sendResult.branding.note}</span>}
                          {(sendResult.recipients || []).map((r) => (
                            <span key={r.user_id} className="ppl-sub">{r.name}: {[["email", r.email_status], ["push", r.push_status], ["sms", r.sms_status]].filter(([, v]) => v).map(([k, v]) => `${k} ${String(v).replace(/_/g, " ")}`).join(" · ")}</span>
                          ))}
                        </div>
                      )}
                      {(acks?.sends || []).length > 0 && (
                        <div className="sop-send-result">
                          <b>Send history — v{doc.version}</b>
                          {acks.sends.map((sh) => (<span key={sh.id} className="ppl-sub">{sh.summary}{sh.sent_by ? ` · by ${sh.sent_by}` : ""}{sh.delivered !== sh.total ? ` · ${sh.delivered}/${sh.total} delivered` : ""}</span>))}
                        </div>
                      )}
                    </form>
                  )}
                </section>
              )}
            </>
          )}
        </main>

        {panel === "review" && doc && !editing && (
          <aside className="sop-review">
            <div className="sop-review-head">
              <div>
                <div className="sop-review-title"><button type="button" className="ppl-link" onClick={() => setPanel(null)} title="Back to the library">‹ Library</button> · Review</div>
                <div className="ppl-sub">v{doc.version} · {doc.is_published ? "published" : "draft"}{review ? ` · ${review.remaining} open` : ""}</div>
              </div>
              <button className="ops-btn" onClick={approve} disabled={doc.is_published} title={doc.is_published ? "Already published" : "Approve = publish"}>{doc.is_published ? "Published" : `Approve v${doc.version}`}</button>
            </div>
            <div className="sop-thread">
              {!review && !reviewNote && <div className="sop-msg is-assistant is-system"><div className="sop-msg-text">Loading the conversation…</div></div>}
              {(review?.turns || []).map((t) => (
                <div key={t.seq} className={`sop-msg ${t.role === "manager" ? "is-manager" : "is-assistant"} ${t.pending ? "is-system" : ""}`}>
                  {t.role !== "manager" && t.sop_version ? <div className="sop-msg-label">Redrafted as v{t.sop_version}</div> : null}
                  <div className="sop-msg-text">{t.text}</div>
                  {t.diff ? (
                    <>
                      <button type="button" className="ppl-link" onClick={() => setOpenDiffSeq(openDiffSeq === t.seq ? null : t.seq)}>{openDiffSeq === t.seq ? "Hide what changed" : "What changed"}</button>
                      {openDiffSeq === t.seq && (
                        <div className="sop-diff sop-diff--inline">
                          {parseUnified(t.diff).map((l, i) => <div key={i} className={`sop-diff-line ${l.t}`}>{(l.t === "add" ? "+ " : "− ") + l.s}</div>)}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              ))}
              {review && !revising && (
                <div className="sop-msg is-assistant">
                  {review.next_question
                    ? <><div className="sop-msg-label">Open question · {(review.open_questions?.length || 0) - review.remaining + 1} of {review.open_questions?.length || review.remaining}</div><div className="sop-msg-text">{review.next_question}</div></>
                    : <div className="sop-msg-text">{doc.is_published ? "This version is published. Ask for a change and I’ll redraft it as a new draft for your approval." : "No open questions left. Ask for a change, or approve it as written."}</div>}
                </div>
              )}
              {revising && <div className="sop-msg is-assistant is-system"><div className="sop-msg-text">Redrafting…</div></div>}
              {reviewNote && <div className="sop-msg is-assistant is-system"><div className="sop-msg-text">{reviewNote}</div></div>}
            </div>
            <form className="sop-composer" onSubmit={sendReview}>
              <input value={composer} onChange={(e) => setComposer(e.target.value)} placeholder={review?.next_question ? "Answer in plain language…" : "Ask for a change…"} disabled={revising || !review} />
              <button type="submit" className="ops-btn" disabled={revising || !review}>{revising ? "…" : "Send"}</button>
            </form>
          </aside>
        )}
      </div>
    </div>
  );
}
